import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { CSS_CLASSES } from "../constants/app";
import {
  filterReportTypeOptions,
  getReportTypeLabel,
  groupReportTypeOptions,
  type ReportTypeOption,
} from "../constants/reportTypes";

const DEBOUNCE_MS = 180;

/** List padding + one category header + three 44px option rows. */
export const REPORT_TYPE_DROPDOWN_HEIGHT_CLASS = "h-[176px]";

type DropdownMode = "absolute" | "inline";

type ReportTypeComboboxProps = {
  id?: string;
  value: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
  dropdownMode?: DropdownMode;
  /** Scrollable list height. Use fixed `h-*` (not only `max-h-*`) to avoid layout shift while filtering. */
  dropdownListClassName?: string;
  onOpenChange?: (open: boolean) => void;
};

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

export default function ReportTypeCombobox({
  id: idProp,
  value,
  onChange,
  disabled = false,
  error = false,
  dropdownMode = "absolute",
  dropdownListClassName = "max-h-60",
  onOpenChange,
}: ReportTypeComboboxProps) {
  const generatedId = useId();
  const inputId = idProp ?? generatedId;
  const listboxId = `${inputId}-listbox`;

  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpenState] = useState(false);
  const [inputText, setInputText] = useState(() =>
    value ? getReportTypeLabel(value) ?? "" : ""
  );
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const setIsOpen = useCallback(
    (open: boolean) => {
      setIsOpenState(open);
      onOpenChange?.(open);
    },
    [onOpenChange]
  );

  const debouncedQuery = useDebouncedValue(inputText, DEBOUNCE_MS);

  const filteredOptions = useMemo(
    () => filterReportTypeOptions(debouncedQuery),
    [debouncedQuery]
  );

  const groupedOptions = useMemo(
    () => groupReportTypeOptions(filteredOptions),
    [filteredOptions]
  );

  const selectableOptions = useMemo(
    () => groupedOptions.flatMap((group) => group.options),
    [groupedOptions]
  );

  const optionIndexByValue = useMemo(() => {
    const map = new Map<string, number>();
    selectableOptions.forEach((opt, index) => {
      map.set(opt.value, index);
    });
    return map;
  }, [selectableOptions]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setHighlightIndex(-1);
  }, [debouncedQuery, isOpen]);

  useEffect(() => {
    if (highlightIndex >= selectableOptions.length && selectableOptions.length > 0) {
      setHighlightIndex(selectableOptions.length - 1);
    }
  }, [highlightIndex, selectableOptions.length]);

  useEffect(() => {
    if (!value) {
      return;
    }
    const label = getReportTypeLabel(value);
    if (label) {
      setInputText(label);
    }
  }, [value]);

  useEffect(() => {
    if (dropdownMode === "inline") {
      return;
    }
    const handlePointerDown = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        if (value) {
          setInputText(getReportTypeLabel(value) ?? "");
        }
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [value, dropdownMode, setIsOpen]);

  const selectOption = useCallback(
    (opt: ReportTypeOption) => {
      onChange(opt.value);
      setInputText(opt.label);
      setIsOpen(false);
    },
    [onChange, setIsOpen]
  );

  const handleInputChange = (next: string) => {
    setInputText(next);
    setIsOpen(true);
    if (value && next !== getReportTypeLabel(value)) {
      onChange("");
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && (event.key === "ArrowDown" || event.key === "Enter")) {
      setIsOpen(true);
      return;
    }
    if (!isOpen) {
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightIndex((i) => {
        if (i < 0) {
          return selectableOptions.length > 0 ? 0 : -1;
        }
        return i < selectableOptions.length - 1 ? i + 1 : i;
      });
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightIndex((i) => {
        if (i <= 0) {
          return selectableOptions.length > 0 ? 0 : -1;
        }
        return i - 1;
      });
    } else if (
      event.key === "Enter" &&
      highlightIndex >= 0 &&
      selectableOptions[highlightIndex]
    ) {
      event.preventDefault();
      selectOption(selectableOptions[highlightIndex]);
    } else if (event.key === "Escape") {
      setIsOpen(false);
      if (value) {
        setInputText(getReportTypeLabel(value) ?? "");
      }
    }
  };

  const inputClassName = [
    CSS_CLASSES.INPUTS.PATIENT,
    error ? "border-red-500" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const listPositionClass =
    dropdownMode === "inline"
      ? "relative mt-2 w-full"
      : "absolute z-50 mt-1 w-full";

  const listbox = isOpen && !disabled && (
    <ul
      id={listboxId}
      role="listbox"
      className={`${listPositionClass} ${dropdownListClassName} overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg`}
      style={{ touchAction: "pan-y" }}
      onMouseLeave={() => setHighlightIndex(-1)}
    >
      {selectableOptions.length === 0 ? (
        <li className="px-3 py-3 text-sm text-guardian-body">
          No matching report types
        </li>
      ) : (
        groupedOptions.map((group, groupIndex) => (
          <li key={group.category} role="presentation" className="list-none">
            <div
              className={[
                "px-3 py-2 text-xs font-semibold text-guardian-header bg-guardian-10 border-t border-guardian-20",
                groupIndex === 0 ? "border-t-0" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {group.category}
            </div>
            <ul role="group" aria-label={group.category} className="list-none p-0 m-0">
              {group.options.map((opt) => {
                const optionIndex = optionIndexByValue.get(opt.value) ?? 0;
                return (
                  <li
                    key={opt.value}
                    role="option"
                    aria-selected={value === opt.value}
                    className={[
                      "cursor-pointer px-3 py-3 text-sm text-guardian-header min-h-[44px] flex items-center hover:bg-gray-100",
                      highlightIndex >= 0 && optionIndex === highlightIndex
                        ? "bg-gray-100"
                        : "",
                      value === opt.value ? "font-medium" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectOption(opt)}
                    onMouseEnter={() => setHighlightIndex(-1)}
                  >
                    {opt.label}
                  </li>
                );
              })}
            </ul>
          </li>
        ))
      )}
    </ul>
  );

  return (
    <div
      ref={containerRef}
      className={dropdownMode === "inline" ? "" : "relative"}
    >
      <input
        id={inputId}
        type="text"
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-autocomplete="list"
        autoComplete="off"
        disabled={disabled}
        className={inputClassName}
        value={inputText}
        placeholder="Search report type…"
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => {
          if (!disabled) {
            setIsOpen(true);
          }
        }}
        onKeyDown={handleKeyDown}
      />
      {listbox}
    </div>
  );
}
